import { useEffect, useState } from 'react';
import type { User } from '@octery/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { navigate, useCurrentPath } from '@/app/navigation';
import { AppLayout } from '@/app/layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/toaster';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { InvitePage } from '@/features/auth/InvitePage';
import { DashboardPage } from '@/features/analytics/DashboardPage';
import { KnowledgeBasePage } from '@/features/knowledge-base/KnowledgeBasePage';
import { AISettingsPage } from '@/features/ai-settings/AISettingsPage';
import { ConversationsPage } from '@/features/conversations/ConversationsPage';
import { ConversationThreadPage } from '@/features/conversations/ConversationThreadPage';
import { CompanySettingsPage } from '@/features/workspace/CompanySettingsPage';
import { PartnersPage } from '@/features/partners/PartnersPage';
import { PartnerDetailPage } from '@/features/partners/PartnerDetailPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const path = useCurrentPath();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const logout = useAuthStore((s) => s.logout);
  const [isSessionCheckComplete, setIsSessionCheckComplete] = useState(false);

  const isInviteRoute = /^\/invite\/[^/]+$/.test(path);

  const isPublicRoute = path === '/login'
    || path === '/register'
    || path === '/forgot-password'
    || path === '/reset-password'
    || isInviteRoute;

  useEffect(() => {
    let cancelled = false;

    if (isPublicRoute || isAuthenticated) {
      setIsSessionCheckComplete(true);
      return () => {
        cancelled = true;
      };
    }

    setIsSessionCheckComplete(false);

    void api
      .get<User>('/api/auth/me')
      .then((user) => {
        if (!cancelled) {
          setAuth(user);
        }
      })
      .catch(() => {
        if (!cancelled) {
          logout();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSessionCheckComplete(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isPublicRoute, logout, setAuth]);

  // Public routes
  if (path === '/login') return <LoginPage />;
  if (path === '/register') return <RegisterPage />;
  if (path === '/forgot-password') return <ForgotPasswordPage />;
  if (path === '/reset-password') return <ResetPasswordPage />;
  if (isInviteRoute) return <InvitePage />;

  if (!isSessionCheckComplete) {
    return null;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const isAdmin = user?.isSystemAdmin ?? false;

  // Protected routes
  const page = (() => {
    switch (true) {
      case path === '/':
        return <DashboardPage />;
      case /^\/conversations\/[^/]+$/.test(path):
        return <ConversationThreadPage />;
      case path === '/conversations':
        return <ConversationsPage />;
      case path === '/knowledge-base':
        return <KnowledgeBasePage />;
      case path === '/ai-settings':
        return <AISettingsPage />;
      case path === '/settings':
        return <CompanySettingsPage />;
      case path === '/partners':
        if (!isAdmin) { navigate('/'); return null; }
        return <PartnersPage />;
      case /^\/partners\/[^/]+$/.test(path):
        if (!isAdmin) { navigate('/'); return null; }
        return <PartnerDetailPage />;
      default:
        return (
          <div>
            <h1 className="text-3xl font-bold">404</h1>
            <p className="text-muted-foreground">Page not found</p>
          </div>
        );
    }
  })();

  return <AppLayout>{page}</AppLayout>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppRoutes />
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
